import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CustomerPageLayout from "../../customer-page-layout/CustomerPageLayout";
import { TransactionCard } from "../components/transaction-card/TransactionCard";
import styles from "./WalletHistoryPage.module.scss";
import { useLazyGetWalletTransactionsQuery } from "../../../../service/wallet-service";
import { WalletTransactionResponse } from "../models/WalletTransactionResponse";
import { formatCurrency } from "../../../../utils";
import { CountryCode, countryCurrencies } from "../../../../model/common/country";
import { RootState } from "../../../../stores/store";
import { TransactionArrowDownIcon, HistIcon } from "../../../../assets/icons";

const PAGE_SIZE = 10;
const LOAD_THRESHOLD_PX = 100;

const formatTxDate = (dateString: string): string => {
	const date = new Date(dateString);
	const datePart = date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
	const timePart = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).toLowerCase();
	return `${datePart}, ${timePart}`;
};

const getIndicatorIcon = (indicator: string) => {
	if (indicator === "C") return <TransactionArrowDownIcon />;
	if (indicator === "D") return <TransactionArrowDownIcon style={{ transform: "rotate(180deg)" }} />;
	return <HistIcon />;
};

export const WalletHistoryPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const countryCode = useSelector((state: RootState) => state.user.countryCode);
	const currency = countryCurrencies[countryCode as CountryCode] ?? "NGN";

	const [transactions, setTransactions] = useState<WalletTransactionResponse[]>([]);
	const [hasMore, setHasMore] = useState(true);

	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const sentinelRef = useRef<HTMLDivElement | null>(null);
	const observerRef = useRef<IntersectionObserver | null>(null);

	const [getWalletTransactions, { isFetching, error }] = useLazyGetWalletTransactionsQuery();

	const hasMoreRef = useRef(hasMore);
	hasMoreRef.current = hasMore;

	const isLoadingPageRef = useRef(false);
	const currentPageRef = useRef(1);

	const loadMore = useCallback(() => {
		if (isLoadingPageRef.current || !hasMoreRef.current) return;
		const nextPage = currentPageRef.current + 1;
		currentPageRef.current = nextPage;
		fetchTransactionsRef.current(nextPage, false);
	}, []);

	const loadMoreRef = useRef(loadMore);
	loadMoreRef.current = loadMore;

	const checkIfAtBottom = useCallback(() => {
		const container = scrollContainerRef.current;
		if (!container || !hasMoreRef.current || isLoadingPageRef.current) return;
		const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
		if (distanceFromBottom < LOAD_THRESHOLD_PX) {
			loadMoreRef.current();
		}
	}, []);

	const checkIfAtBottomRef = useRef(checkIfAtBottom);
	checkIfAtBottomRef.current = checkIfAtBottom;

	const setupObserver = useCallback(() => {
		if (observerRef.current) {
			observerRef.current.disconnect();
			observerRef.current = null;
		}
		if (!sentinelRef.current || !scrollContainerRef.current) return;

		observerRef.current = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && hasMoreRef.current && !isLoadingPageRef.current) {
					loadMoreRef.current();
				}
			},
			{ root: scrollContainerRef.current, threshold: 0 }
		);
		observerRef.current.observe(sentinelRef.current);
	}, []);

	const setupObserverRef = useRef(setupObserver);
	setupObserverRef.current = setupObserver;

	const fetchTransactions = useCallback((page: number, reset: boolean) => {
		isLoadingPageRef.current = true;

		getWalletTransactions({ page, pageSize: PAGE_SIZE }, false)
			.unwrap()
			.then(response => {
				const newTransactions = response.items || [];
				setTransactions(prev => (reset ? newTransactions : [...prev, ...newTransactions]));
				setHasMore(response.hasNextPage);
				isLoadingPageRef.current = false;
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						checkIfAtBottomRef.current();
						setupObserverRef.current();
					});
				});
			})
			.catch(() => {
				setHasMore(false);
				isLoadingPageRef.current = false;
			});
	}, [getWalletTransactions]);

	const fetchTransactionsRef = useRef(fetchTransactions);
	fetchTransactionsRef.current = fetchTransactions;

	const setScrollContainer = useCallback((node: HTMLDivElement | null) => {
		scrollContainerRef.current = node;
		setupObserverRef.current();
	}, []);

	const setSentinel = useCallback((node: HTMLDivElement | null) => {
		sentinelRef.current = node;
		setupObserverRef.current();
	}, []);

	useEffect(() => {
		fetchTransactionsRef.current(1, true);
	}, []);

	const getAmountText = (transaction: WalletTransactionResponse) => {
		const amountText = formatCurrency(Math.abs(transaction.amount), { currency });
		if (transaction.debitCreditIndicator === "C") return `+${amountText}`;
		if (transaction.debitCreditIndicator === "D") return `-${amountText}`;
		return amountText;
	};

	return (
		<CustomerPageLayout showBackButton title={t("common.labels.walletTransactions")} onBack={() => navigate(-1)}>
			<div className={styles.transactionListWrapper}>
				<div className={styles.transactionList} ref={setScrollContainer}>
					{transactions.length > 0 ? (
						<>
							{transactions.map(transaction => (
								<TransactionCard
									key={transaction.id}
									icon={getIndicatorIcon(transaction.debitCreditIndicator)}
									leftTitle={transaction.remarks || ""}
									leftSubTitle={formatTxDate(transaction.dateCreated)}
									rightTitle={getAmountText(transaction)}
									status={t("common.labels.completed")}
								/>
							))}
							{isFetching && (
								<div className={styles.loadingMore}>
									<div className={styles.spinner} />
									<span>{t("history.web.loadingMore")}</span>
								</div>
							)}
							{hasMore && <div ref={setSentinel} style={{ height: "1px" }} />}
							{error && (
								<div className={styles.errorItem}>
									<p>{t("history.web.failedToLoadTransactions")}</p>
									<button className={styles.retryButton} onClick={() => fetchTransactionsRef.current(currentPageRef.current, false)}>
										{t("energyManagement.retry")}
									</button>
								</div>
							)}
							{!hasMore && !error && (
								<div className={styles.endOfList}>
									<p>{t("history.web.reachedTheEnd")}</p>
								</div>
							)}
						</>
					) : error ? (
						<div className={styles.errorEmpty}>
							<p>{t("history.web.failedToLoadTransactions")}</p>
							<button className={styles.retryButton} onClick={() => fetchTransactionsRef.current(1, true)}>
								{t("energyManagement.retry")}
							</button>
						</div>
					) : isFetching ? (
						<div className={styles.loadingMore}>
							<div className={styles.spinner} />
							<span>{t("history.web.loadingTransactions")}</span>
						</div>
					) : (
						<div className={styles.emptyState}>
							<HistIcon />
							<p>{t("history.yourTransactionsWillAppearHere")}</p>
						</div>
					)}
				</div>
			</div>
		</CustomerPageLayout>
	);
};
